import React, {
  useEffect,
  useRef,
  useState,
  memo,
  useMemo,
  useCallback,
} from "react";
import PropTypes from "prop-types";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye, faEyeSlash } from "@fortawesome/free-solid-svg-icons";
import { Tooltip } from "@mui/material";

function Input({
  name,
  value = "",
  placeholder = "",
  type = "text",
  disabled = false,
  readOnly = false,
  autoFocus = false,
  maxLength,
  min,
  max,
  onChange,
  handleChange,
  onFocus,
  onBlur,
  onKeyDown,
  getInputNumber,
  getInputPhoneNumber,
  getInputText,
  label,
  customLabelDiv,
  requiredField = false,
  required = false,
  error,
  message,
  errorDivCustomStyles,
  className,
  isPasswordField = false,
  passwordInputDivStyles,
  toggleEyeIcon,
  toggleEyeIconState = false,
  regeneratePasswordOption = false,
  regeneratePassword = false,
  onClickRegeneratePasswordIcon,
}) {
  const inputRef = useRef(null);
  const [isFocused, setIsFocused] = useState(false);

  useEffect(() => {
    if (autoFocus && inputRef.current) {
      inputRef.current.focus();
    }
  }, [autoFocus]);

  const onChangeInput = useCallback(
    (e) => {
      const inputValue = e.target.value;
      const number = inputValue.replace(/\D/g, "");
      const text = inputValue.replace(/[^a-zA-Z]/g, "");

      if (handleChange) {
        handleChange(e);
      }

      if (number.length >= 0 && getInputNumber) {
        // Create a new event with filtered value instead of mutating the original
        const filteredEvent = {
          ...e,
          target: {
            ...e.target,
            value: number,
          },
        };
        getInputNumber(filteredEvent);
      }

      if (
        number.length >= 0 &&
        number.length <= 10 &&
        getInputPhoneNumber
      ) {
        // Create a new event with filtered value instead of mutating the original
        const filteredEvent = {
          ...e,
          target: {
            ...e.target,
            value: number.slice(0, 10),
          },
        };
        getInputPhoneNumber(filteredEvent);
      }

      if (text.length >= 0 && getInputText) {
        // Create a new event with filtered value instead of mutating the original
        const filteredEvent = {
          ...e,
          target: {
            ...e.target,
            value: text,
          },
        };
        getInputText(filteredEvent);
      }
    },
    [handleChange, getInputNumber, getInputPhoneNumber, getInputText]
  );

  const handleFocus = useCallback((e) => {
    setIsFocused(true);
    if (onFocus) {
      onFocus(e);
    }
  }, [onFocus]);

  const handleBlur = useCallback((e) => {
    setIsFocused(false);
    if (onBlur) {
      onBlur(e);
    }
  }, [onBlur]);

  const getInputClasses = useMemo(() => {
    const baseClasses =
      "w-full rounded-lg border-2 bg-white py-2.5 px-3 text-gray-900 placeholder-gray-400 outline-none transition-all duration-200 ease-in-out font-medium shadow-sm min-h-[44px]";

    // Sync with dropdown border colors exactly
    const focusClasses =
      "focus:border-[#3b82f6] focus:ring-2 focus:ring-[rgba(59,130,246,0.1)] focus:shadow-lg focus:bg-white";
    const errorClasses = error
      ? "border-[#ef4444] focus:border-[#ef4444] focus:ring-2 focus:ring-[rgba(239,68,68,0.1)] bg-red-50/30"
      : "border-[#d1d5db] hover:border-[#9ca3af] hover:shadow-md hover:bg-white";
    const disabledClasses = disabled
      ? "bg-[#f3f4f6] cursor-not-allowed text-gray-400 border-[#d1d5db] shadow-none"
      : "";
    const darkModeClasses =
      "dark:bg-gray-800 dark:border-gray-600 dark:text-white dark:placeholder-gray-400 dark:focus:border-blue-400 dark:hover:bg-gray-700/50";

    return `${baseClasses} ${focusClasses} ${errorClasses} ${disabledClasses} ${darkModeClasses} ${
      className || ""
    }`;
  }, [error, disabled, className]);

  const getLabelClasses = () => {
    const baseClasses =
      "block text-sm font-semibold mb-1.5 transition-colors duration-200";
    const getColorClasses = () => {
      if (error) return "text-[#ef4444]";
      if (isFocused) return "text-[#3b82f6]";
      return "text-gray-700 dark:text-gray-300";
    };

    const colorClasses = getColorClasses();

    return `${baseClasses} ${colorClasses}`;
  };

  return (
    <div className="mb-2">
      {label && (
        <div className={getLabelClasses()}>
          {customLabelDiv ? (
            <div className="flex flex-row justify-between items-center">
              <span className="flex items-center gap-1">
                {label}
                {(requiredField || required) && (
                  <span className="text-red-500 text-base">*</span>
                )}
              </span>
              <div className="text-xs text-gray-500">
                {customLabelDiv}
              </div>
            </div>
          ) : (
            <span className="flex items-center gap-1">
              {label}
              {(requiredField || required) && (
                <span className="text-red-500 text-base">*</span>
              )}
            </span>
          )}
        </div>
      )}

      <div
        className={`relative group ${
          isPasswordField ? passwordInputDivStyles || "" : ""
        }`}
      >
        {/* Input field with enhanced styling */}
        <input
          ref={inputRef}
          autoComplete="off"
          name={name}
          placeholder={placeholder}
          value={value}
          disabled={disabled}
          className={getInputClasses}
          onChange={onChange || onChangeInput}
          type={type}
          readOnly={readOnly}
          onKeyDown={onKeyDown}
          onFocus={handleFocus}
          onBlur={handleBlur}
          maxLength={maxLength}
          min={min}
          max={max}
        />

        {/* Password regeneration icon */}
        {regeneratePasswordOption && !regeneratePassword ? (
          <Tooltip title="Regenerate Password" arrow placement="top">
            <button
              type="button"
              onClick={onClickRegeneratePasswordIcon}
              className="h-6 w-6 absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-blue-600 hover:text-blue-700 bg-transparent border-none outline-none p-0 flex items-center justify-center"
              aria-label="Regenerate Password"
            >
              <img
                src="/images/reset-password.png"
                alt="Reset Password"
                className="h-6 w-6"
              />
            </button>
          </Tooltip>
        ) : (
          isPasswordField && (
            <Tooltip
              title={
                !toggleEyeIconState ? "Show Password" : "Hide Password"
              }
              arrow
              placement="top"
            >
              <button
                type="button"
                onClick={toggleEyeIcon}
                className="absolute right-3 top-1/2 transform -translate-y-1/2 cursor-pointer text-gray-500 hover:text-blue-600 bg-transparent border-none outline-none p-0 flex items-center justify-center"
                aria-label={
                  toggleEyeIconState ? "Hide Password" : "Show Password"
                }
              >
                <FontAwesomeIcon
                  icon={toggleEyeIconState ? faEyeSlash : faEye}
                  className="w-5 h-5"
                />
              </button>
            </Tooltip>
          )
        )}
      </div>

      {/* Enhanced error/message display */}
      <div
        className={`mt-0.5 min-h-[0.875rem] flex items-center gap-2 text-xs transition-all duration-200 ${
          error ? "text-[#ef4444]" : "text-[#3b82f6]"
        } ${error || message ? "opacity-100" : "opacity-0"} ${
          errorDivCustomStyles || ""
        }`}
      >
        {error && (
          <svg
            className="w-4 h-4 text-red-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z"
              clipRule="evenodd"
            />
          </svg>
        )}
        {message && !error && (
          <svg
            className="w-4 h-4 text-blue-500 flex-shrink-0"
            fill="currentColor"
            viewBox="0 0 20 20"
          >
            <path
              fillRule="evenodd"
              d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z"
              clipRule="evenodd"
            />
          </svg>
        )}
        <span>{error || message}</span>
      </div>
    </div>
  );
}

Input.propTypes = {
  // Basic input props
  name: PropTypes.string,
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  placeholder: PropTypes.string,
  type: PropTypes.string,
  disabled: PropTypes.bool,
  readOnly: PropTypes.bool,
  autoFocus: PropTypes.bool,
  maxLength: PropTypes.number,
  min: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  max: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),

  // Event handlers
  onChange: PropTypes.func,
  handleChange: PropTypes.func,
  onFocus: PropTypes.func,
  onBlur: PropTypes.func,
  onKeyDown: PropTypes.func,

  // Specialized input handlers
  getInputNumber: PropTypes.func,
  getInputPhoneNumber: PropTypes.func,
  getInputText: PropTypes.func,

  // Label and validation
  label: PropTypes.string,
  customLabelDiv: PropTypes.node,
  requiredField: PropTypes.bool,
  required: PropTypes.bool,
  error: PropTypes.string,
  message: PropTypes.string,
  errorDivCustomStyles: PropTypes.string,

  // Styling
  className: PropTypes.string,

  // Password field specific
  isPasswordField: PropTypes.bool,
  passwordInputDivStyles: PropTypes.string,
  toggleEyeIcon: PropTypes.func,
  toggleEyeIconState: PropTypes.bool,
  regeneratePasswordOption: PropTypes.bool,
  regeneratePassword: PropTypes.bool,
  onClickRegeneratePasswordIcon: PropTypes.func,
};

export default memo(Input);
